import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type {
  Deliverable,
  DeliverableType,
  DeliverableStatus,
  DeliverableUrl,
  DeliverableCustomField,
  DeliverableEntry,
} from '@/types'
import type { DeliverablesState } from './state'

export interface CreateDeliverableData {
  projectId: string
  name: string
  type: DeliverableType
  description?: string
  status?: DeliverableStatus
  operatorIds?: string[]
  tags?: string[]
  urls?: DeliverableUrl[]
  // Rich base fields
  responsiblePerson?: string
  startDate?: Date
  endDate?: Date
  budget?: number
  currency?: string
  location?: string
  priority?: 'critical' | 'high' | 'medium' | 'low'
  notes?: string
  customFields?: DeliverableCustomField[]
  // Context document IDs
  contextDocumentId?: string
  agentDocumentId?: string
  planningDocumentId?: string
  // Service fields
  serviceType?: string
  provider?: string
  sla?: string
  contractEnd?: Date
  recurrence?: string
  // Infrastructure fields
  infrastructureType?: string
  environment?: string
  region?: string
  capacity?: string
  monthlyCost?: number
  specifications?: string
  // Research fields
  hypothesis?: string
  methodology?: string
  findings?: string
  datasets?: string[]
  publications?: string[]
  // Component fields
  componentType?: string
  supplier?: string
  quantity?: number
  unitCost?: number
  // Hardware fields
  model?: string
  manufacturer?: string
  serialNumber?: string
  warrantyEnd?: Date
}

export type UpdateDeliverableData = Partial<Omit<CreateDeliverableData, 'projectId'>>

const stripUndefined = (obj: Record<string, unknown>): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  )
}

export const createActions = (state: DeliverablesState) => {
  const { logActivity } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const toDate = (v: unknown): Date | undefined => {
    if (!v) return undefined
    if ((v as any)?.toDate) return (v as any).toDate()
    if (v instanceof Date) return v
    return undefined
  }

  const mapDeliverableDoc = (d: { id: string; data: () => Record<string, unknown> }): Deliverable => {
    const data = d.data()
    const mapped = {
      ...data,
      id: d.id,
      operatorIds: data.operatorIds || [],
      tags: data.tags || [],
      urls: data.urls || [],
      customFields: data.customFields || [],
      pinnedDocuments: data.pinnedDocuments || [],
      entries: ((data.entries || []) as Record<string, unknown>[]).map((e) => ({
        ...e,
        date: toDate(e.date) || new Date(),
        createdAt: toDate(e.createdAt) || new Date(),
      })),
      startDate: toDate(data.startDate),
      endDate: toDate(data.endDate),
      contractEnd: toDate(data.contractEnd),
      warrantyEnd: toDate(data.warrantyEnd),
      createdAt: toDate(data.createdAt) || new Date(),
      updatedAt: toDate(data.updatedAt) || new Date(),
    }
    return mapped as unknown as Deliverable
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(collection(db, 'deliverables'), ...baseConstraints, orderBy('createdAt', 'desc'))

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        q = query(collection(db, 'deliverables'), ...baseConstraints, where('organizationId', '==', authStore.user.organizationId), orderBy('createdAt', 'desc'))
      } else {
        // No org assigned — return empty to prevent seeing all tenant deliverables
        state.isLoading = false
        return
      }
    } else if (authStore.user?.role === 'operator') {
      q = query(collection(db, 'deliverables'), ...baseConstraints, where('operatorIds', 'array-contains', authStore.user.id), orderBy('createdAt', 'desc'))
    }

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.deliverables = snapshot.docs.map(mapDeliverableDoc)
      state.isLoading = false
    }, (error) => {
      console.error('Deliverables subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchDeliverables = async (projectId?: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      let q = query(collection(db, 'deliverables'), ...baseConstraints, orderBy('createdAt', 'desc'))

      if (projectId) {
        q = query(
          collection(db, 'deliverables'),
          ...baseConstraints,
          where('projectId', '==', projectId),
          orderBy('createdAt', 'desc')
        )
      } else if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'deliverables'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        } else {
          state.isLoading = false
          return
        }
      } else if (authStore.user?.role === 'operator') {
        q = query(
          collection(db, 'deliverables'),
          ...baseConstraints,
          where('operatorIds', 'array-contains', authStore.user.id),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      state.deliverables = snapshot.docs.map(mapDeliverableDoc)
    } catch (error) {
      console.error('Error fetching deliverables:', error)
      state.error = 'Error al cargar los entregables'
    } finally {
      state.isLoading = false
    }
  }

  const fetchDeliverableById = async (id: string): Promise<Deliverable | null> => {
    try {
      const docRef = doc(db, 'deliverables', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const deliverable = mapDeliverableDoc(docSnap)
        state.selectedDeliverable = deliverable
        return deliverable
      }

      return null
    } catch (error) {
      console.error('Error fetching deliverable:', error)
      state.error = 'Error al cargar el entregable'
      return null
    }
  }

  const createDeliverable = async (data: CreateDeliverableData): Promise<string> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const projectRef = doc(db, 'projects', data.projectId)
      const projectSnap = await getDoc(projectRef)

      if (!projectSnap.exists()) {
        throw new Error('Proyecto no encontrado')
      }

      const projectData = projectSnap.data()

      const deliverableData = stripUndefined({
        ...getTenantData(),
        ...data,
        organizationId: projectData.organizationId,
        operatorIds: data.operatorIds || projectData.operatorIds || [],
        status: data.status || 'planning',
        tags: data.tags || [],
        urls: data.urls || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      })

      const docRef = await addDoc(collection(db, 'deliverables'), deliverableData)

      await logActivity(
        'deliverable',
        docRef.id,
        'created',
        `Se creo el entregable "${data.name}"`
      )

      await notificationsStore.notifyAdminsAndOperators(
        'deliverable_created',
        'Nuevo entregable creado',
        `Se ha creado el entregable "${data.name}"`,
        'deliverable',
        docRef.id
      )

      await fetchDeliverables(data.projectId)

      return docRef.id
    } catch (error) {
      console.error('Error creating deliverable:', error)
      state.error = 'Error al crear el entregable'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateDeliverable = async (id: string, data: UpdateDeliverableData): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const updateData = stripUndefined({
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      await updateDoc(doc(db, 'deliverables', id), updateData)

      await logActivity(
        'deliverable',
        id,
        data.status ? 'status_changed' : 'updated',
        data.status
          ? `Se cambio el estado del entregable a "${data.status}"`
          : 'Se actualizo el entregable'
      )

      const index = state.deliverables.findIndex((d) => d.id === id)
      if (index !== -1) {
        state.deliverables[index] = {
          ...state.deliverables[index],
          ...data,
          updatedAt: new Date(),
        } as Deliverable
      }

      if (state.selectedDeliverable?.id === id) {
        state.selectedDeliverable = {
          ...state.selectedDeliverable,
          ...data,
          updatedAt: new Date(),
        } as Deliverable
      }
    } catch (error) {
      console.error('Error updating deliverable:', error)
      state.error = 'Error al actualizar el entregable'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteDeliverable = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const deliverable = state.deliverables.find((d) => d.id === id)

      await deleteDoc(doc(db, 'deliverables', id))

      await logActivity(
        'deliverable',
        id,
        'deleted',
        `Se elimino el entregable "${deliverable?.name || id}"`
      )

      state.deliverables = state.deliverables.filter((d) => d.id !== id)

      if (state.selectedDeliverable?.id === id) {
        state.selectedDeliverable = null
      }
    } catch (error) {
      console.error('Error deleting deliverable:', error)
      state.error = 'Error al eliminar el entregable'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const setFilters = (filters: Partial<DeliverablesState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearFilters = (): void => {
    state.filters = {
      search: '',
      projectId: '',
      type: '',
      status: '',
    }
  }

  const setSelectedDeliverable = (deliverable: Deliverable | null): void => {
    state.selectedDeliverable = deliverable
  }

  const addEntry = async (deliverableId: string, entry: Omit<DeliverableEntry, 'id' | 'createdAt' | 'createdBy'>): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const newEntry: DeliverableEntry = {
        id: (() => { try { return crypto.randomUUID() } catch { return Math.random().toString(36).substring(2) + Date.now().toString(36) } })(),
        ...entry,
        createdAt: new Date(),
        createdBy: userId,
      }

      const firestoreEntry = stripUndefined({ ...newEntry })

      await updateDoc(doc(db, 'deliverables', deliverableId), {
        entries: arrayUnion(firestoreEntry),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      await logActivity('deliverable', deliverableId, 'updated', `Se agrego registro "${entry.title}"`)

      // Update local
      const d = state.deliverables.find((d) => d.id === deliverableId)
      if (d) {
        if (!d.entries) d.entries = []
        d.entries.push(newEntry)
      }
      if (state.selectedDeliverable?.id === deliverableId) {
        if (!state.selectedDeliverable.entries) state.selectedDeliverable.entries = []
        state.selectedDeliverable.entries.push(newEntry)
      }
    } catch (error) {
      console.error('Error adding entry:', error)
      state.error = 'Error al agregar registro'
      throw error
    }
  }

  const removeEntry = async (deliverableId: string, entryId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const d = state.deliverables.find((d) => d.id === deliverableId)
      const entry = d?.entries?.find((e) => e.id === entryId)
      if (!entry) return

      await updateDoc(doc(db, 'deliverables', deliverableId), {
        entries: arrayRemove(stripUndefined({ ...entry })),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      // Update local
      if (d && d.entries) {
        d.entries = d.entries.filter((e) => e.id !== entryId)
      }
      if (state.selectedDeliverable?.id === deliverableId && state.selectedDeliverable.entries) {
        state.selectedDeliverable.entries = state.selectedDeliverable.entries.filter((e) => e.id !== entryId)
      }
    } catch (error) {
      console.error('Error removing entry:', error)
      state.error = 'Error al eliminar registro'
      throw error
    }
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchDeliverables,
    fetchDeliverableById,
    createDeliverable,
    updateDeliverable,
    deleteDeliverable,
    addEntry,
    removeEntry,
    setFilters,
    clearFilters,
    setSelectedDeliverable,
  }
}
